import { useState, useEffect } from "react";
import { Radio } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { useLang } from "@/context/LanguageContext";

const SESSIONS = [
  { name: "Sydney", start: 22, end: 7 },
  { name: "Tokyo", start: 0, end: 9 },
  { name: "Londres", start: 8, end: 17 },
  { name: "New York", start: 13, end: 22 },
];

const CLOCKS = [
  { label: "Paris", tz: "Europe/Paris" },
  { label: "Londres", tz: "Europe/London" },
  { label: "New York", tz: "America/New_York" },
  { label: "Tokyo", tz: "Asia/Tokyo" },
];

const LOCAL_TZ = "America/Toronto";

function activeSessions(d) {
  const h = d.getUTCHours() + d.getUTCMinutes() / 60;
  return SESSIONS.filter((s) =>
    s.start < s.end ? h >= s.start && h < s.end : h >= s.start || h < s.end
  ).map((s) => s.name);
}

export function SessionBar() {
  const { t, lang } = useLang();
  const loc = lang === "en" ? "en-US" : "fr-FR";
  const cityName = (n) => (n === "Londres" ? t("sb.london") : n);
  const timeIn = (d, tz) => new Intl.DateTimeFormat(loc, {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz,
  }).format(d);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const news = useNovexData("/novex/news", { live: true, liveMs: 30000 });
  const items = news.data?.items || [];
  const ticker = items.slice(0, 8);

  const sessions = activeSessions(now);
  const localDate = new Intl.DateTimeFormat(loc, {
    weekday: "short", day: "numeric", month: "short", timeZone: LOCAL_TZ,
  }).format(now);

  return (
    <div className="overflow-hidden rounded-lg border border-[#222733] bg-[#0B0E14]" data-testid="session-bar">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-[#222733] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t("sb.session")}</span>
          <span className="rounded bg-blue-600/20 px-2 py-0.5 font-mono text-xs font-bold uppercase text-blue-300" data-testid="active-session">
            {sessions.length ? sessions.map(cityName).join(" · ") : t("sb.calm")}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 capitalize">{localDate}</span>
          <span className="font-mono text-sm font-bold text-white" data-testid="local-time">{timeIn(now, LOCAL_TZ)}</span>
          <span className="text-[10px] text-slate-600">Toronto</span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1">
          {CLOCKS.map((c) => (
            <div key={c.label} className="flex items-center gap-1.5" data-testid={`clock-${c.label}`}>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{cityName(c.label)}</span>
              <span className="font-mono text-xs text-slate-300">{timeIn(now, c.tz)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 px-4 py-2">
        <span className="flex shrink-0 items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
          <Radio className="h-3.5 w-3.5 animate-pulse" /> Squawk
        </span>
        <div className="relative flex-1 overflow-hidden">
          {ticker.length ? (
            <div className="flex animate-[marquee_50s_linear_infinite] gap-8 whitespace-nowrap">
              {[...ticker, ...ticker].map((it, i) => (
                <span key={i} className="text-xs text-slate-400">
                  <span className="mr-1.5 font-mono text-[10px] text-slate-600">
                    {it.created_at ? new Date(it.created_at).toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                  {it.formatted_text}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-slate-600">{t("sb.loadingFeed")}</span>
          )}
        </div>
      </div>
    </div>
  );
}
